import { Box, Typography } from "@mui/material";
import type { JSX } from "react";

import { dateOnly } from "../../lib/format.js";
import { PageHero, Section } from "./blocks.js";
import type { LegalPageContent } from "./legal-content.js";
import { usePageMeta } from "./usePageMeta.js";

/**
 * One layout for the two legal pages (`/terms`, `/privacy`): the public
 * site's hero, then numbered sections of prose. The text is data
 * (`legal-content.ts`) so the pages cannot drift apart in shape.
 */
export function LegalPage({
  content,
  meta,
}: {
  content: LegalPageContent;
  meta: { title: string; description: string };
}): JSX.Element {
  usePageMeta(meta);

  return (
    <>
      <PageHero title={content.title} lede={content.lede} />
      <Section id="legal">
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 3 }}>
          Last updated {dateOnly(content.updated)}
        </Typography>
        <Box component="article" sx={{ maxWidth: 780, display: "grid", gap: 3 }}>
          {content.sections.map((section) => (
            <Box component="section" key={section.id} id={section.id} aria-labelledby={`${section.id}-h`}>
              <Typography
                component="h2"
                id={`${section.id}-h`}
                sx={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em", mb: 1 }}
              >
                {section.title}
              </Typography>
              {section.paragraphs.map((paragraph, index) => (
                <Typography key={index} sx={{ fontSize: 14.5, lineHeight: 1.7, color: "text.secondary", mb: 1 }}>
                  {paragraph}
                </Typography>
              ))}
              {section.bullets && (
                <Box component="ul" sx={{ m: 0, pl: 3, display: "grid", gap: 0.5 }}>
                  {section.bullets.map((bullet, index) => (
                    <Typography component="li" key={index} sx={{ fontSize: 14.5, lineHeight: 1.6, color: "text.secondary" }}>
                      {bullet}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Section>
    </>
  );
}
